/**
 * What a staff member is told after a resend attempt, and where the failures
 * split (BON-630).
 *
 * THE SPLIT IS NOT THE SAME SHAPE AS THE RESERVATIONS ROTATION ENDPOINTS
 * (`reservationRotationOutcome.ts`), because THIS endpoint's failure contract
 * is different in one load-bearing way: a send failure here is its own typed
 * status (502 `EMAIL_SEND_FAILED`), not a 200 the caller has to inspect. So
 * there are three destinations, not two:
 *
 *   1. `resentOutcome` — 200. A toast, and a note when the address changed.
 *   2. `sendFailedOutcome` / `indeterminateOutcome` — 502, other 5xx, or no
 *      response. The dialog CLOSES for these: either the send is known to have
 *      failed, or the outcome is unknown, and either way there is nothing left
 *      in the form worth keeping open. A must-dismiss alert takes over.
 *   3. Everything else (400, 403, 404, 409) — classified as `inline` by
 *      {@link classifyResendFailure} and NEVER reaches this file's outcome
 *      builders. The dialog stays open and the typed address is preserved,
 *      because the server decided nothing was written before answering (see
 *      the BON-630 contract's handler order) — there is nothing here to
 *      acknowledge, only something to fix and retry.
 */
import {
  BookingConfirmationError,
  NO_RESPONSE_STATUS,
  type ResendBookingConfirmationResult,
} from '../../api/booking-confirmation';
import type { ErrorDialogTone } from '../../shared/ErrorDialog';

export interface ResendConfirmationAlert {
  title: string;
  message: string;
  tone: ErrorDialogTone;
}

export interface ResendConfirmationOutcome {
  /** A green confirmation. Mutually exclusive with `alert`. */
  toast?: string;
  /** Something staff has to acknowledge. */
  alert?: ResendConfirmationAlert;
  /** Whether the booking + Activity card must be re-read. */
  refresh: boolean;
}

/** RED, not amber — both destinations here leave staff needing to double
 *  check what happened, which is the more serious of the two dialog tones. */
const ALERT_TONE: ErrorDialogTone = 'error';

const SEND_FAILED_TITLE = 'The confirmation email was not sent';

export function resentOutcome(result: ResendBookingConfirmationResult): ResendConfirmationOutcome {
  const contactNote = result.contactUpdated ? ' The email on file was updated.' : '';
  return { toast: `Confirmation sent to ${result.emailedTo}.${contactNote}`, refresh: true };
}

/** The email did NOT go out. `err.contactUpdated` can still be true here —
 *  the correction commits before the send is attempted (contract handler
 *  order) — so a kept correction has to be said even though the send failed. */
export function sendFailedOutcome(err: BookingConfirmationError): ResendConfirmationOutcome {
  const contactNote = err.contactUpdated
    ? ' The email on file was updated even though the email could not be sent.'
    : '';
  return {
    alert: {
      title: SEND_FAILED_TITLE,
      message: `${err.message}${contactNote} Try again in a moment.`,
      tone: ALERT_TONE,
    },
    refresh: true,
  };
}

const UNKNOWN_TITLE = 'Not sure whether that went through';
// NOT the Activity card: CSRs and RDs cannot see it (Admin-only today), so
// telling them to check it sends most callers of this dialog to a dead end.
const UNKNOWN_MESSAGE =
  'We couldn’t reach the server, so this may or may not have gone through. Wait a minute and try ' +
  'again. If the first one went out, you’ll be told a confirmation was just sent.';

export function indeterminateOutcome(): ResendConfirmationOutcome {
  return {
    alert: { title: UNKNOWN_TITLE, message: UNKNOWN_MESSAGE, tone: ALERT_TONE },
    refresh: true,
  };
}

export type ResendFailureClassification =
  | {
      kind: 'inline';
      /** Written for staff, safe to show verbatim under the fields. */
      message: string;
      /** True only for `BOOKING_NOT_CONFIRMED` — the one 409 whose refusal
       *  means the booking on screen is already stale. */
      refresh: boolean;
    }
  | { kind: 'settle'; outcome: ResendConfirmationOutcome };

/**
 * Where a thrown resend error goes: inline in the still-open dialog, or out to
 * the caller as a settled outcome that closes it.
 *
 * ORDER MATTERS. 502 is checked before the general `>= 500` test because it is
 * the one 5xx this endpoint types — everything else in that range (and no
 * response at all) is the genuinely unknown case.
 */
export function classifyResendFailure(err: unknown): ResendFailureClassification {
  if (!(err instanceof BookingConfirmationError)) {
    return { kind: 'settle', outcome: indeterminateOutcome() };
  }
  if (err.status === 502) {
    return { kind: 'settle', outcome: sendFailedOutcome(err) };
  }
  if (err.status === NO_RESPONSE_STATUS || err.status >= 500) {
    return { kind: 'settle', outcome: indeterminateOutcome() };
  }
  // 400, 403, 404, or a 409 other than BOOKING_NOT_CONFIRMED: the server
  // decided nothing before answering, so the form stays open and the typed
  // address is preserved.
  return { kind: 'inline', message: err.message, refresh: err.code === 'BOOKING_NOT_CONFIRMED' };
}
